"use client";

import { usePathname, useSearchParams } from "next/navigation";
import { useMemo } from "react";
import type { DIYAggregation } from "~/graphql/shared/page";
import { FILTER_KEY, decodeFilters, encodeFilters } from "./utils";

interface UseDIYFilterProps {
	aggregation: DIYAggregation;
}

export function useDIYFilter({ aggregation }: UseDIYFilterProps) {
	const searchParams = useSearchParams();
	const filters = useMemo(() => decodeFilters(searchParams?.get(FILTER_KEY) ?? ""), [searchParams]);

	const activeOptions = useMemo(
		() => filters[aggregation.value] ?? [],
		[aggregation.value, filters],
	);

	return {
		activeOptions,
		filters,
	} as const;
}

interface UseDIYFilterOptionProps {
	aggregation: DIYAggregation;
	option: DIYAggregation["options"][number];
}

export function useDIYFilterOption({ aggregation, option }: UseDIYFilterOptionProps) {
	const searchParams = useSearchParams();
	const pathname = usePathname();
	const { filters, activeOptions } = useDIYFilter({ aggregation });

	const isActive = useMemo(() => activeOptions.includes(option.value), [activeOptions, option]);

	const href = useMemo(() => {
		const newOptions = isActive
			? activeOptions.filter((value) => value !== option.value)
			: [...activeOptions, option.value];

		newOptions.sort();

		const newFilters =
			newOptions.length > 0
				? {
						...filters,
						[aggregation.value]: newOptions,
					}
				: Object.fromEntries(Object.entries(filters).filter(([key]) => key !== aggregation.value));

		const query = {
			...Object.fromEntries(searchParams?.entries() ?? []),
			[FILTER_KEY]: encodeFilters(newFilters),
		};

		return { pathname, query } as const;
	}, [isActive, activeOptions, option.value, filters, aggregation.value, searchParams, pathname]);

	return {
		isActive,
		href,
		activeOptions,
	} as const;
}
